import { IconNotes, IconPencilCheck, IconStack2 } from "@tabler/icons-react"
import { OUTSIDE_BOOKING_SEASON_LABEL } from "../lib/bookingSeason"
import { DAILY_LIMIT_LABEL } from "../lib/dayGrid"
import type { SlotData } from "../lib/dayGrid"
import { isRecordedWithoutScheduling } from "../lib/manualRecord"
import type { SlotDecoration } from "../lib/rescheduleTargets"
import { SLOT_TYPE_LABEL, slotTypeLabelForSlug, slotTypeOf } from "../lib/slotTypes"
import type { StaffColumn } from "../lib/staffColumns"

/** The "Recorded" marker's explanation (AB#868). */
const RECORDED_TITLE = "Recorded without scheduling: not in the scheduling system, and the household was not notified"

export function SlotCell({ cell, staff, onClick, bookable = false, loading = false, selection }: {
  cell: SlotData
  staff: StaffColumn
  onClick: () => void
  /**
   * Whether a free row here can be booked into — by this user, as this row's kind (`bookableSlotTypes`).
   * False leaves open rows as the labels they have always been — a cell that looks clickable and does
   * nothing is worse than one that never offered, which is the same rule the column headers follow.
   */
  bookable?: boolean
  /**
   * What belongs in this cell is still on its way. Drawn as a placeholder whatever `cell` says, since
   * what it says is either the day before's or an empty grid's — and neither is this one's.
   */
  loading?: boolean
  /**
   * Selection mode (the manual reschedule workspace, AB#811): what this cell is for the booking being
   * placed. Omitted draws the cell exactly as the calendar always has.
   */
  selection?: SlotDecoration
}) {
  // Nothing to click: a placeholder offers neither an appointment nor a slot to book.
  if (loading) {
    return <div className="booking-slot loading slot-shimmer" aria-hidden />
  }

  // A pairing not yet applied lands here: highlighted, named for its booking, and the way to undo it.
  if (selection?.kind === "pending") {
    const who = selection.label ?? "an appointment"
    // The slot type it moves to (ams-8ia): named always, and called out when the move changes it.
    const typeLabel = slotTypeLabelForSlug(selection.eventTypeSlug)
    const typeText = typeLabel ? (selection.typeChange ? `${typeLabel} (new type)` : typeLabel) : null
    const name = `Pending move of ${who} to ${staff.name} at ${cell.time}${typeText ? ` as ${typeText}` : ""}. Select to undo it.`
    return (
      <button
        type="button"
        className="booking-slot selection-pending"
        style={{ borderColor: staff.color }}
        title={name}
        aria-label={name}
        aria-pressed
        onClick={onClick}
      >
        <div className="slot-badge badge-pending">Pending</div>
        <div className="slot-time" style={{ color: staff.color }}>{cell.time}</div>
        <div className="slot-name">{who}</div>
        {typeText && <div className="slot-type" data-testid="pending-slot-type">{typeText}</div>}
      </button>
    )
  }

  // The booking being placed may move here. Offered whatever the grid would otherwise say about the row
  // — the server's rules, not Cal's published hours, decide a manual move.
  if (selection?.kind === "valid") {
    const who = selection.label ?? "the selected appointment"
    // A cell of another slot type changes the appointment's type (ams-8ia): said before the click.
    const newType = selection.typeChange ? slotTypeLabelForSlug(selection.eventTypeSlug) : null
    const name = newType
      ? `${staff.name} at ${cell.time} can take ${who} as ${newType}. Select to move it here; its slot type becomes ${newType}.`
      : `${staff.name} at ${cell.time} can take ${who}. Select to move it here.`
    return (
      <button
        type="button"
        className="booking-slot open selection-valid"
        title={name}
        aria-label={name}
        onClick={onClick}
      >
        <div className="slot-badge badge-open">Move here</div>
        <div className="slot-time">{cell.time}</div>
        {newType && <div className="slot-type" data-testid="valid-slot-type">as {newType}</div>}
      </button>
    )
  }

  // It may not, and says why: drawn as the row it is, with nothing to click.
  const locked = selection?.kind === "invalid"
  const lockedReason = selection?.reason ?? "Not available for the selected appointment"
  const lockedClass = locked ? " selection-invalid" : ""
  const lockedText = locked && <span className="sr-only">{lockedReason}</span>
  // Where the booking being placed is now.
  const origin = selection?.kind === "active-origin"

  // Nobody is working this slot. Drawn as its own state rather than as an open slot with a note,
  // because an open slot is an invitation to book one and this is the opposite.
  if (cell.state === "unavailable") {
    // The office's own break or lunch. Hatched like a real absence, since neither can be booked, but
    // in the warmer grey and named for what it is instead of joining the count of people who are
    // out.
    const isBreak = cell.unavailable?.scheduledBreak === true
    const isSiteClosure = cell.unavailable?.scope === "event-type"
    // The host has filled their day (AB#606): named for that, because "Unavailable" would read as an
    // absence and send somebody looking for who covers it.
    const isDailyLimit = cell.unavailable?.dailyLimit === true
    return (
      <div
        className={`booking-slot unavailable ${isBreak ? "scheduled-break" : ""} ${isSiteClosure ? "site-closed" : ""} ${isDailyLimit ? "daily-limit" : ""}${lockedClass}`}
        title={locked ? lockedReason : `${cell.unavailable?.label ?? "Unavailable"} · ${cell.time}`}
      >
        <div className={`slot-badge ${isBreak ? "badge-break" : "badge-unavailable"}`}>
          {isBreak ? cell.unavailable?.label : isSiteClosure ? "Closed" : isDailyLimit ? DAILY_LIMIT_LABEL : "Unavailable"}
        </div>
        <div className="slot-time">{cell.time}</div>
        {/* The reason, when somebody wrote one. "Unavailable" twice over would be noise. */}
        {cell.unavailable?.reason && <div className="slot-type">{cell.unavailable.reason}</div>}
        {lockedText}
      </div>
    )
  }

  if (cell.state === "open") {
    // Offered only when nothing in selection mode says otherwise.
    const offer = bookable && !locked
    // Open to staff, not to the public. Only ever a modifier on an open cell, never a state of its
    // own: whoever is at the counter can still book it, so it keeps the open cell's button and
    // dashed edge and takes none of the unavailable hatching, which means "nobody can". Drawn on free
    // rows alone — an appointment already booked is managed the same in or out of season, and an
    // unavailable row is shut to everybody already.
    const outsideSeason = cell.outsideBookingSeason === true
    const seasonClass = outsideSeason ? "outside-booking-season" : ""
    const seasonHint = outsideSeason ? ` · ${OUTSIDE_BOOKING_SEASON_LABEL}` : ""
    // The dots are the hint for a sighted reader; this is the same hint for everyone else.
    const seasonText = outsideSeason && <span className="sr-only">{OUTSIDE_BOOKING_SEASON_LABEL}</span>

    // Free, but only for a private or managed appointment (AB#779) — the site publishes no public
    // hours here. Marked with the same leading stripe a booked one of that kind carries, so the edge
    // means one thing across the grid: this row is that kind. Whether it is taken is what the fill
    // already says.
    const slotType = slotTypeOf(cell)
    if (slotType !== "public") {
      // A button only when there is somewhere to go and this user may book this kind. Booking one of
      // these books its own event type rather than intake, which is what the label is saying.
      const kindClass = slotType === "managed" ? "off-slot" : "private"
      const label = SLOT_TYPE_LABEL[slotType]
      const Cell = offer ? "button" : "div"
      return (
        <Cell
          className={`booking-slot open ${kindClass} ${seasonClass}${lockedClass}`}
          title={locked ? lockedReason : (offer
            ? `Book a ${label.toLowerCase()} appointment with ${staff.name} at ${cell.time}`
            : `${label} appointment · ${cell.time}`) + seasonHint}
          {...(offer ? { type: "button" as const, onClick } : {})}
        >
          <div className={`slot-badge ${slotType === "managed" ? "badge-offslot-open" : "badge-private-open"}`}>{label}</div>
          <div className="slot-time">{cell.time}</div>
          <div className="slot-type">{offer ? "Book" : "Open"}</div>
          {seasonText}
          {lockedText}
        </Cell>
      )
    }

    const Cell = offer ? "button" : "div"
    return (
      <Cell
        className={`booking-slot open ${seasonClass}${lockedClass}`}
        title={locked ? lockedReason : offer
          ? `Book an appointment with ${staff.name} at ${cell.time}${seasonHint}`
          : outsideSeason ? `${OUTSIDE_BOOKING_SEASON_LABEL} · ${cell.time}` : undefined}
        {...(offer ? { type: "button" as const, onClick } : {})}
      >
        <div className="slot-badge badge-open">Open</div>
        <div className="slot-time">{cell.time}</div>
        {offer && <div className="slot-type">Book</div>}
        {seasonText}
        {lockedText}
      </Cell>
    )
  }

  // The leading stripe: a managed appointment, or one somebody has to look at for where it sits —
  // off the slot cadence, or outside business hours. A private one carries its own colour of edge.
  const bookedType = slotTypeOf(cell)
  const misplaced = !!cell.offGrid || !!cell.outsideBusinessHours
  const isOff = bookedType === "managed" || (bookedType === "public" && misplaced)
  const isPrivate = bookedType === "private"
  const kindClass = isOff ? "off-slot" : isPrivate ? "private" : ""
  const badge = bookedType !== "public" ? SLOT_TYPE_LABEL[bookedType] : misplaced ? "Off-grid" : undefined

  // An appointment standing in time somebody has since said they are not there for — a date override,
  // which is the only thing that puts a mark on a booked row. Kept as an appointment and marked, never
  // hidden behind the unavailable shading: it is a booking somebody has to move, and it can only be
  // moved by someone who can see it.
  const whileAway = !!cell.unavailable

  // Whose absence it is. A site closure and one person being out are different problems — the first
  // needs the whole day's appointments moved, the second needs this one covered — so they are not
  // worded alike, and the closure carries the same modifier a closed free row does.
  const isSiteClosure = cell.unavailable?.scope === "event-type"

  // Selection mode's own marks on an appointment: the one being placed, or one nothing may move onto.
  const selectionClass = origin ? " selection-origin" : lockedClass

  // The rows a longer appointment covers. Drawn as a continuation of the block above rather than as
  // a second appointment: a 60-minute booking is one appointment spanning two 30-minute rows.
  if (cell.continuation) {
    return (
      <div
        className={`booking-slot continuation ${kindClass} ${whileAway ? "while-away" : ""} ${isSiteClosure ? "site-closed" : ""}${selectionClass}`}
        style={{ backgroundColor: staff.light, borderColor: `${staff.color}40` }}
        title={locked ? lockedReason : undefined}
        onClick={locked ? undefined : onClick}
      />
    )
  }

  const spans = (cell.spanRows ?? 1) > 1

  // This appointment and every one it collides with — what clicking the cell lists.
  const overlapCount = cell.hasOverlap ? 1 + (cell.overlapping?.length ?? 0) : 0

  // Staff notes on this appointment, counted by the server with the day's bookings. A count only:
  // the notes themselves are read in the dialog.
  const noteCount = cell.booking?.noteCount ?? 0
  const notesLabel = `Has notes (${noteCount})`

  // Recorded after its time had passed, with no booking in the scheduling system and nothing sent to
  // the household (AB#868). Otherwise drawn like any appointment: it took place, and is given an
  // outcome the same way.
  const recorded = isRecordedWithoutScheduling(cell.booking)

  // Not a button while it is locked: nothing may move onto it, and opening it is not what this mode is for.
  const Booked = locked ? "div" : "button"
  return (
    <Booked
      {...(locked ? {} : { type: "button" as const })}
      className={`booking-slot ${kindClass} ${spans ? "spanning" : ""} ${whileAway ? "while-away" : ""} ${isSiteClosure ? "site-closed" : ""} ${overlapCount > 0 ? "has-overlap" : ""}${selectionClass}`}
      // The colour only, not the `background` shorthand: an inline shorthand would also clear the
      // hatching `.while-away` lays over it.
      style={{ backgroundColor: staff.light, borderColor: `${staff.color}40` }}
      title={locked ? lockedReason : origin ? "The selected appointment's current time" : overlapCount > 0 ? "Overlapping appointments — click to see them all" : undefined}
      {...(locked ? {} : { onClick })}
      {...(origin ? { "aria-current": true as const } : {})}
    >
      {origin && <div className="slot-badge badge-origin">Selected</div>}
      {badge && <div className={`slot-badge ${isPrivate ? "badge-private" : "badge-offslot"}`}>{badge}</div>}
      {/* The appointment's own start, which is not the row's when it is off the cadence. */}
      <div className="slot-time" style={{ color: staff.color }}>{cell.bookedAt ?? cell.time}</div>
      <div className="slot-name">{cell.client ?? cell.booking?.attendees?.at(0)?.name ?? "Booked"}</div>
      {cell.siteName && <div className="slot-type">{cell.siteName}</div>}
      {recorded && (
        <div className="slot-recorded" data-testid="recorded-marker" title={RECORDED_TITLE}>
          <IconPencilCheck className="size-3 shrink-0" aria-hidden />
          Recorded
        </div>
      )}
      {noteCount > 0 && (
        <div className="slot-notes" role="img" aria-label={notesLabel} title={notesLabel}>
          <IconNotes className="size-3 shrink-0" aria-hidden />
          {noteCount}
        </div>
      )}
      {/* A count rather than a bare warning: it says how many appointments clicking will list. */}
      {overlapCount > 0 && (
        <div className="slot-overlap">
          <IconStack2 className="size-3 shrink-0" aria-hidden />
          {overlapCount} overlapping
        </div>
      )}
      {/* The reason somebody entered, which is what makes this appointment a problem — "Out sick",
          "Snow closure". Falls back to naming whose absence it is, since the override may carry no
          reason at all and the scope is still worth saying. */}
      {whileAway && (
        <div className="slot-conflict">
          {cell.unavailable?.reason ?? (isSiteClosure ? "Site closed" : "Host unavailable")}
        </div>
      )}
      {lockedText}
    </Booked>
  )
}
